import { Model, Request, Requestor } from '@viddsee/server-api-client';
import { SeriesCollectionVideo } from '@/scripts/models/SeriesCollectionVideo';
import { identifiers as seriesIdentifierDefinitions } from '@/scripts/models/videoCollections/identifierDefinitions/series';
import { commonApi } from '@/scripts/webcall/v3/common';
import { CollectionImplementation, CollectionImplementationData, CollectionImplementationOptions } from './core/BaseCollectionImplementation';

export class SeriesCollectionImplementation extends CollectionImplementation {
  constructor({ video, params }: CollectionImplementationOptions) {
    super({
      video,
      params,
      identifierDefinitions: seriesIdentifierDefinitions,
    });
  }

  test(): boolean {
    return !!this.params.series;
  }

  provide(): CollectionImplementationData {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const implementation = this;
    return new CollectionImplementationData({
      implementation,
      fetchers: {
        async info(): Promise<Model.VideoCollection.Info | null> {
          const webcallPromises = [
            commonApi.get({ url: implementation.collectionApiRequestPath }) as Promise<Requestor.ResponseWrapper<Model.VideoCollection.Info>>,
          ];

          const parentAccessPaths = implementation.identifierValues.slice(0, -1);
          if (parentAccessPaths.length > 0) {
            const accessUrl = `/${(
              parentAccessPaths
                .map(({ ids, value }) => [ids.apiId, value].join('/'))
                .join('/')
            )}`;

            webcallPromises.push(
              commonApi.get({ url: accessUrl }) as Promise<Requestor.ResponseWrapper<Model.VideoCollection.Info>>,
            );
          }

          const requestResponses = await Promise.all(webcallPromises);
          const videoCollectionInfos = requestResponses.map(({ data: videoCollectionInfo }) => videoCollectionInfo);

          const { series, series_season: season } = videoCollectionInfos.reduce((acc, videoCollectionInfo) => {
            acc[videoCollectionInfo.type] = videoCollectionInfo;
            return acc;
          }, {} as Record<Model.VideoCollection.InfoType, Model.VideoCollection.Info | undefined>);

          if (!series) {
            // TODO: Log this to sentry
            // eslint-disable-next-line no-console
            console.error('Series object cannot be absent');
            return null;
          }

          return implementation.mergeSeriesSeasonInfo({
            seriesInfo: series,
            seasonInfo: season,
          });
        },
        async videoIndex(params): Promise<number> {
          if (!implementation.video) return -1;

          try {
            const { data: collectionIndexResponse } = await commonApi.get({ url: `${implementation.collectionApiRequestPath}/video_index` }, {
              videoId: implementation.video.id,
              ...params,
            }) as Requestor.ResponseWrapper<Model.Video.FindCollectionIndexResponse>;
            return collectionIndexResponse.index;
          } catch (error) {
            return -1;
          }
        },
        async videos(params): Promise<Model.Pagination.PaginatedItemsResponse<Model.Video.Info>> {
          const { data: PaginatedVideoInfo } = await commonApi.get({ url: `${implementation.collectionApiRequestPath}/videos` }, {
            order: Request.Pagination.OptionOrder.Ascending,
            ...params,
          }) as Requestor.ResponseWrapper<Model.Pagination.PaginatedItemsResponse<Model.Video.Info>>;
          return PaginatedVideoInfo;
        },
      },
      transformers: {
        video(video: Model.Video.Info): SeriesCollectionVideo {
          return new SeriesCollectionVideo(video, implementation.identifierValues);
        },
      },
    });
  }

  mergeSeriesSeasonInfo({ seriesInfo, seasonInfo }: { seriesInfo: Model.VideoCollection.Info; seasonInfo?: Model.VideoCollection.Info }): Model.VideoCollection.Info {
    if (!seasonInfo) return seriesInfo;

    const seasonTitle = seasonInfo.title.trim();
    const seasonTitlePresentation = (
      seasonTitle || (
        seriesInfo.subcollectionCount > 1
          ? `Season ${seasonInfo.slug}`
          : ''
      )
    );

    return ({
      ...seriesInfo,
      ...seasonInfo,
      title: [
        seriesInfo.title,
        seasonTitlePresentation,
      ]
        .filter(x => x)
        .join(': '),
      description: seasonInfo.description || seriesInfo.description || null,
      photoLargeUrl: seasonInfo.photoLargeUrl || seriesInfo.photoLargeUrl || null,
      photoMediumUrl: seasonInfo.photoMediumUrl || seriesInfo.photoMediumUrl || null,
      photoSmallUrl: seasonInfo.photoSmallUrl || seriesInfo.photoSmallUrl || null,
    });
  }
}
